import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import axios from "axios";
import "./styles/theme.css";
import "./App.css";
import "./styles/Global.css";
import "./styles/Pages.css";
import SyncPage from "./components/SyncPage";
import LandingPage from "./components/LandingPage";
import HomePage from "./components/HomePage";
import SettingsPage from "./components/SettingsPage";
import HelpPage from "./components/HelpPage";
import SiteNav from "./components/SiteNav";
import { getDiscordAvatarUrl } from "./utils/discord";

// IMPORT LEGAL COMPONENTS
import TermsOfService from "./components/legal/TermsOfService";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import VerifyStartPage from "./components/VerifyStartPage";
import VerifyCallbackPage from "./components/VerifyCallbackPage";
import ProfilePage from "./components/ProfilePage";
import FamilyPage from "./components/FamilyPage";
import TranscriptPage from "./components/TranscriptPage";
import DonatePage from "./components/DonatePage";
import AdminPage from "./components/AdminPage";
import { COMMUNITY_INVITE } from "./data/links";
import {
  sanitizeReturnTo,
  writeSessionCookie,
  clearSessionCookie,
  readSessionCookie,
} from "./utils/session";

const API_BASE = process.env.REACT_APP_API_BASE;
const GUILD_SYNC_TTL_MS = 10 * 60 * 1000;

const getInitialJSON = (key, fallback) => {
  const saved = localStorage.getItem(key);
  if (!saved) return fallback;
  try {
    return saved.startsWith("{") || saved.startsWith("[")
      ? JSON.parse(saved)
      : fallback;
  } catch {
    return fallback;
  }
};

function safeParseJSON(raw, fallback = null) {
  if (raw == null || raw === "") return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export default function App() {
  const [user, setUser] = useState(() => getInitialJSON("kami_user", null));
  const [guilds, setGuilds] = useState(() => getInitialJSON("kami_guilds", []));
  const [, setMemberGuilds] = useState(() =>
    getInitialJSON("kami_member_guilds", []),
  );
  const [serverStats, setServerStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isBotOwner, setIsBotOwner] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    clearSessionCookie();
    window.location.href = "/";
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dataRaw = params.get("data");

    const syncData = async (token, currentSavedUser) => {
      try {
        const response = await axios.get(`${API_BASE}/auth/refresh-guilds`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const freshGuilds = response.data.guilds;
        const freshMemberGuilds = response.data.memberGuilds || [];
        setGuilds(freshGuilds);
        setMemberGuilds(freshMemberGuilds);
        localStorage.setItem("kami_guilds", JSON.stringify(freshGuilds));
        localStorage.setItem(
          "kami_member_guilds",
          JSON.stringify(freshMemberGuilds),
        );
        localStorage.setItem("kami_guilds_synced_at", String(Date.now()));
        const updatedUser = { ...currentSavedUser, guilds: freshGuilds };
        setUser(updatedUser);
      } catch (err) {
        console.error("Auto-sync failed", err);
        // Keep session on rate limits / transient errors; only clear on auth failure.
        if (err.response?.status === 401) {
          localStorage.removeItem("kami_token");
          clearSessionCookie();
        }
      }
    };

    const initAuth = async () => {
      if (dataRaw) {
        try {
          const decodedData = safeParseJSON(decodeURIComponent(dataRaw), null);
          if (!decodedData || typeof decodedData !== "object") {
            throw new Error("Invalid login payload");
          }
          const userObj = {
            id: decodedData.id,
            username: decodedData.username,
            avatar: decodedData.avatar,
          };
          setUser(userObj);
          setGuilds(decodedData.guilds || []);
          setMemberGuilds(decodedData.memberGuilds || []);
          localStorage.setItem("kami_user", JSON.stringify(userObj));
          localStorage.setItem(
            "kami_guilds",
            JSON.stringify(decodedData.guilds || []),
          );
          localStorage.setItem(
            "kami_member_guilds",
            JSON.stringify(decodedData.memberGuilds || []),
          );
          localStorage.setItem("kami_avatar", getDiscordAvatarUrl(userObj));
          if (decodedData.token)
            localStorage.setItem("kami_token", decodedData.token);
          localStorage.setItem("kami_guilds_synced_at", String(Date.now()));
          if (decodedData.token) {
            writeSessionCookie({
              id: userObj.id,
              username: userObj.username,
              avatar: userObj.avatar,
              token: decodedData.token,
            });
          }
          let returnTo = sanitizeReturnTo(params.get("returnTo"));
          if (!returnTo) {
            try {
              returnTo = sanitizeReturnTo(
                sessionStorage.getItem("kami_return_to"),
              );
              sessionStorage.removeItem("kami_return_to");
            } catch {
              /* ignore */
            }
          }
          // Full navigation so React Router picks up the post-login route.
          window.location.replace(returnTo || "/servers");
          return;
        } catch (e) {
          console.error("Error parsing login data:", e);
        }
      } else {
        try {
          const savedUserStr = localStorage.getItem("kami_user");
          const savedToken = localStorage.getItem("kami_token");
          const savedGuilds = localStorage.getItem("kami_guilds");
          const savedMemberGuilds = localStorage.getItem("kami_member_guilds");
          if (savedUserStr && savedUserStr.startsWith("{")) {
            const parsedUser = safeParseJSON(savedUserStr, null);
            if (parsedUser) {
              setUser(parsedUser);
              if (savedToken) {
                writeSessionCookie({
                  id: parsedUser.id,
                  username: parsedUser.username,
                  avatar: parsedUser.avatar,
                  token: savedToken,
                });
                const lastSync = Number(
                  localStorage.getItem("kami_guilds_synced_at") || 0,
                );
                const stale = Date.now() - lastSync > GUILD_SYNC_TTL_MS;
                if (stale) await syncData(savedToken, parsedUser);
              }
            }
          } else {
            // www ↔ apex: recover slim session from shared cookie.
            const fromCookie = readSessionCookie();
            if (fromCookie) {
              const userObj = {
                id: fromCookie.id,
                username: fromCookie.username,
                avatar: fromCookie.avatar,
              };
              setUser(userObj);
              localStorage.setItem("kami_user", JSON.stringify(userObj));
              localStorage.setItem("kami_token", fromCookie.token);
              localStorage.setItem(
                "kami_avatar",
                getDiscordAvatarUrl(userObj),
              );
              await syncData(fromCookie.token, userObj);
            }
          }
          const parsedGuilds = safeParseJSON(savedGuilds, null);
          if (Array.isArray(parsedGuilds)) setGuilds(parsedGuilds);
          const parsedMemberGuilds = safeParseJSON(savedMemberGuilds, null);
          if (Array.isArray(parsedMemberGuilds)) {
            setMemberGuilds(parsedMemberGuilds);
          }
        } catch (e) {
          console.error("Error restoring session:", e);
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  useEffect(() => {
    if (guilds.length === 0) return;

    let cancelled = false;
    const targets = guilds.filter((g) => g.hasBot);

    const loadStats = async () => {
      for (const guild of targets) {
        if (cancelled) break;
        try {
          const res = await fetch(`${API_BASE}/guild-stats/${guild.id}`);
          const data = await res.json();
          if (!cancelled && data) {
            setServerStats((prev) => ({ ...prev, [guild.id]: data }));
          }
        } catch {
          if (!cancelled) {
            setServerStats((prev) => ({
              ...prev,
              [guild.id]: { memberCount: "N/A", ownerName: "Unknown" },
            }));
          }
        }
        // Space requests out so Discord doesn't 429 the bot token.
        await new Promise((r) => setTimeout(r, 400));
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [guilds]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("kami_token");
    if (!user?.id || !token) {
      setIsBotOwner(false);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/admin/access`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setIsBotOwner(Boolean(data?.isOwner));
      } catch {
        if (!cancelled) setIsBotOwner(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return (
    <Router>
      <AppShell
        user={user}
        guilds={guilds}
        setGuilds={setGuilds}
        setMemberGuilds={setMemberGuilds}
        serverStats={serverStats}
        isLoading={isLoading}
        isBotOwner={isBotOwner}
        onLogout={handleLogout}
      />
    </Router>
  );
}

function AppShell({
  user,
  guilds,
  setGuilds,
  setMemberGuilds,
  serverStats,
  isLoading,
  isBotOwner,
  onLogout,
}) {
  const location = useLocation();
  const isTranscript = location.pathname.startsWith("/transcripts/");

  return (
    <div className="app-wrapper">
      <SiteNav user={user} onLogout={onLogout} isBotOwner={isBotOwner} />

      <div className="main-content">
        <Routes>
          <Route
            path="/"
            element={
              isLoading ? (
                <div className="page-loading">
                  <div className="sync-spinner" />
                </div>
              ) : (
                <LandingPage user={user} />
              )
            }
          />
          <Route
            path="/servers"
            element={
              isLoading ? (
                <div className="page-loading">
                  <div className="sync-spinner" />
                </div>
              ) : user ? (
                <div className="container">
                  <HomePage guilds={guilds} />
                </div>
              ) : (
                <LandingPage user={null} />
              )
            }
          />
          <Route
            path="/manage/:guildId"
            element={
              <SettingsPage
                guilds={guilds}
                user={user}
                serverStats={serverStats}
              />
            }
          />
          <Route path="/help" element={<HelpPage guilds={guilds} />} />
          <Route
            path="/sync"
            element={
              <SyncPage
                setGuilds={setGuilds}
                setMemberGuilds={setMemberGuilds}
              />
            }
          />
          <Route
            path="/profile"
            element={
              <div className="container">
                <ProfilePage user={user} isOwn />
              </div>
            }
          />
          <Route
            path="/u/:userId"
            element={
              <div className="container">
                <ProfilePage user={user} isOwn={false} />
              </div>
            }
          />
          <Route path="/transcripts/:id" element={<TranscriptPage />} />
          <Route
            path="/donate"
            element={<DonatePage user={user} guilds={guilds} />}
          />
          <Route
            path="/admin"
            element={
              <div className="container">
                <AdminPage user={user} />
              </div>
            }
          />
          <Route
            path="/global"
            element={<Navigate to="/profile?tab=global" replace />}
          />
          <Route
            path="/shop"
            element={<Navigate to="/profile?tab=shop" replace />}
          />
          <Route
            path="/badges"
            element={<Navigate to="/profile?tab=badges" replace />}
          />
          <Route
            path="/family/:userId"
            element={
              <div className="container">
                <FamilyPage user={user} />
              </div>
            }
          />
          <Route
            path="/family"
            element={<Navigate to="/profile?tab=family" replace />}
          />

          <Route path="/verify/callback" element={<VerifyCallbackPage />} />
          <Route path="/verify/:guildId" element={<VerifyStartPage />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </div>

      {!isTranscript && (
        <footer className="kami-footer">
          <div className="footer-content">
            <p>© 2026 Nocturne • Where the quiet gathers</p>
            <div className="footer-links">
              <a href={COMMUNITY_INVITE} target="_blank" rel="noreferrer">
                Community
              </a>
              <span className="separator">|</span>
              <Link to="/terms">Terms of Service</Link>
              <span className="separator">|</span>
              <Link to="/help">Commands</Link>
              <span className="separator">|</span>
              <Link to="/privacy">Privacy Policy</Link>
              <span className="separator">|</span>
              <a href="mailto:thekamigrove@gmail.com">Contact</a>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
